import React, { useState, useCallback } from 'react';
import { FileText, UploadSimple } from '@phosphor-icons/react';
import TiptapEditor from '../TiptapEditor';

export interface EditFile {
  id: string;
  name: string;
  content: string;
  section: 'memory' | 'context' | 'skill';
  skillGroup?: string;
}

interface EditModePageProps {
  files: EditFile[];
  activeFileName: string | null;
  onFileChange: (files: EditFile[]) => void;
}

/**
 * Editor pane that renders in the Preview area during edit mode.
 * No sidebar or header — those come from the main layout.
 */
export default function EditModePage({ files, activeFileName, onFileChange }: EditModePageProps) {
  const activeFile = activeFileName ? files.find(f => f.name === activeFileName) : files[0];

  const updateFileContent = useCallback((content: string) => {
    if (!activeFile) return;
    onFileChange(files.map(f => f.id === activeFile.id ? { ...f, content } : f));
  }, [activeFile, files, onFileChange]);

  if (!activeFile) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-surface)', color: 'var(--text-muted)', fontSize: 'var(--fs-lg)',
      }}>
        Select a file to edit
      </div>
    );
  }

  const ext = activeFile.name.split('.').pop()?.toLowerCase() || '';
  const isEditable = ['md', 'txt', 'json', 'csv', 'tsv', ''].includes(ext);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Editor toolbar */}
      <div style={{
        height: 44, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', padding: '0 20px', background: 'var(--bg-surface)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={14} weight="light" style={{ color: 'var(--brand)' }} />
          <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>{activeFile.name}</span>
          <span style={{
            fontSize: 'var(--fs-xs)', padding: '2px 7px', borderRadius: 5,
            background: 'var(--bg-accent)', color: 'var(--brand)', fontWeight: 500,
          }}>{activeFile.section === 'context' ? 'context' : activeFile.section === 'skill' ? activeFile.skillGroup || 'skill' : 'memory'}</span>
          {!isEditable && (
            <span style={{ fontSize: 'var(--fs-xs)', padding: '2px 7px', borderRadius: 5, background: '#FFF3E0', color: '#e65100', fontWeight: 500 }}>read-only</span>
          )}
        </div>
      </div>

      {/* Editor content */}
      <div style={{ flex: 1, minHeight: 0, background: 'var(--bg-surface)', overflow: 'hidden' }}>
        {isEditable ? (
          <TiptapEditor
            key={activeFile.id}
            value={activeFile.content}
            onChange={updateFileContent}
          />
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', gap: 16, padding: 32,
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14, background: 'var(--bg-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FileText size={24} weight="light" style={{ color: 'var(--brand)' }} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{activeFile.name}</div>
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-muted)' }}>.{ext} files cannot be edited in the browser</div>
            </div>
            <button
              style={{
                padding: '8px 20px', borderRadius: 8, fontSize: 'var(--fs-md)', fontWeight: 500,
                background: 'var(--bg-surface)', border: '1px solid var(--border-strong)',
                color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <UploadSimple size={14} weight="light" />
              Replace file
            </button>
          </div>
        )}
      </div>

      <style>{`
        div:hover > .edit-file-delete { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
